import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CardIcon,
  CheckIcon,
  FileTextIcon,
  MicrophoneFilledIcon,
  SparklesIcon,
  TranslateIcon,
} from "@/components/Icons";
import AndroidDictationDemoVideo from "@/components/AndroidDictationDemoVideo";
import { OnboardingIntent, Persona } from "@/models/onboardingPersona";
import { getPlatform } from "@/platform";

// Intent-matched "aha" demo shown right before the free-plan review step.
// Pure client-side theater — no network, no permissions — so the step can
// never block the funnel.
//
// Every intent plays the SAME three beats, because the transformation is what
// people are buying. Finished output on its own only shows what a transcript
// looks like, and everyone can already picture that:
//
//   1. THE MESS      what they have now — a 90-minute file, raw spoken words
//   2. 1T WORKING    a short, visible moment of the app doing the work
//   3. THE ARTIFACT  what comes back — a summary, action points, a clean
//                    sentence at the cursor, a flashcard
//
// Beat 3 is the one that has to land: the paywall two screens later charges
// for exactly those artifacts, so they have to have been SEEN first.
//
// Demo content is persona-flavored where it matters (a lawyer sees a
// deposition file, a student a lecture): `usePersonaFlavor` resolves
// `<key>_<persona>` and falls back to the base `<key>` when no variant
// exists — in en.json or in a not-yet-translated locale.

const usePersonaFlavor = (persona: Persona | null) => {
  const { t, i18n } = useTranslation();
  return (baseKey: string) => {
    const flavored = `${baseKey}_${persona}`;
    // Probe the CURRENT language's bundle directly — i18n.exists() resolves
    // through fallbackLng:"en", which would leak English persona variants
    // into locales whose translations lag en.json. Missing in the active
    // locale → use that locale's base copy instead.
    const lang = i18n.resolvedLanguage || i18n.language;
    const hasFlavored =
      !!persona && !!i18n.getResource(lang, "translation", flavored);
    return hasFlavored ? t(flavored) : t(baseKey);
  };
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Advances 0 → delays.length, one beat per entry (ms since the previous).
// Reduced motion jumps straight to the last beat, which is the artifact — the
// informative state.
const useBeats = (delays: number[]) => {
  const [beat, setBeat] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setBeat(delays.length);
      return;
    }
    const timers = delays.map((_, i) =>
      setTimeout(
        () => setBeat(i + 1),
        delays.slice(0, i + 1).reduce((a, b) => a + b, 0),
      ),
    );
    return () => timers.forEach(clearTimeout);
    // `delays` is a per-render literal; keying on length keeps the timer
    // chain from restarting on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delays.length]);
  return beat;
};

// Types `text` one character at a time once `active` is true, then reports
// completion so the caller can schedule the next beat off the real end of the
// line rather than a hardcoded duration — line lengths vary by locale.
const Typewriter = ({
  text,
  active,
  speedMs = 26,
  className,
  onDone,
}: {
  text: string;
  active: boolean;
  speedMs?: number;
  className?: string;
  onDone?: () => void;
}) => {
  const [len, setLen] = useState(0);
  // Held in a ref so an inline `onDone` closure doesn't restart the typing run
  // on every parent render.
  const doneRef = useRef<(() => void) | undefined>(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!active) {
      setLen(0);
      return;
    }
    if (prefersReducedMotion()) {
      setLen(text.length);
      doneRef.current?.();
      return;
    }
    setLen(0);
    let typed = 0;
    const id = setInterval(() => {
      typed += 1;
      setLen(typed);
      if (typed >= text.length) {
        clearInterval(id);
        doneRef.current?.();
      }
    }, speedMs);
    return () => clearInterval(id);
  }, [active, text, speedMs]);

  return (
    <span className={className}>
      {text.slice(0, len)}
      {active && len < text.length && (
        <span className="opacity-60 animate-pulse">▍</span>
      )}
    </span>
  );
};

const Card = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`glass-tile w-full overflow-hidden rounded-3xl text-start ${className}`}>
    {children}
  </div>
);

const Divider = ({ children }: { children: React.ReactNode }) => (
  <div className="border-t border-[var(--hairline)] px-4 py-3 animate-in fade-in slide-in-from-bottom-1 duration-500">
    {children}
  </div>
);

// A live-looking audio meter. Purely decorative, so the bars are deterministic
// rather than random — a re-render must not reshuffle them. Fixed-width bars,
// never flexible: this sits next to text that has to keep its room on a 320px
// phone.
const Waveform = ({ bars = 12, live }: { bars?: number; live: boolean }) => (
  <span
    className="flex h-6 shrink-0 items-center gap-[3px]"
    aria-hidden="true"
  >
    {Array.from({ length: bars }, (_, i) => (
      <span
        key={i}
        className={`aha-wave-bar w-[3px] shrink-0 rounded-full bg-[var(--tint)] ${
          live ? "" : "opacity-30"
        }`}
        style={{
          height: `${8 + ((i * 37) % 16)}px`,
          animationDelay: `${(i % 7) * 90}ms`,
          animationPlayState: live ? "running" : "paused",
        }}
      />
    ))}
  </span>
);

// ── Beat 3 for import and record ─────────────────────────────────────────
// The same artifact for both, deliberately: recording and importing converge
// on one deliverable, and that is the message the paywall needs to inherit.
const NotesArtifact = ({
  beat,
  persona,
}: {
  beat: number;
  persona: Persona | null;
}) => {
  const { t } = useTranslation();
  // Flavored: a deposition transcript followed by a launch-date summary reads
  // as a stock screenshot, which is the opposite of what this beat is for.
  const tp = usePersonaFlavor(persona);
  return (
    <>
      {beat >= 1 && (
        <Divider>
          <p className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wider text-[var(--premium-primary)]">
            <SparklesIcon size={12} />
            {t("onboarding.ahaDemo.summaryLabel")}
          </p>
          <ul className="mt-1.5 flex flex-col gap-1">
            {[1, 2, 3].map((n) => (
              <li
                key={n}
                className="flex gap-1.5 text-[12.5px] leading-snug text-[var(--text-primary)]"
              >
                <span className="text-[var(--text-tertiary)]">•</span>
                <span>{tp(`onboarding.ahaDemo.summary${n}`)}</span>
              </li>
            ))}
          </ul>
        </Divider>
      )}
      {beat >= 2 && (
        <div className="border-t border-[var(--hairline)] px-4 py-2.5 animate-in fade-in duration-500">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--success)]/15 px-2.5 py-1 text-[11px] font-bold text-[var(--success)]">
            <CheckIcon size={12} />
            {t("onboarding.ahaDemo.actionsChip")}
          </span>
        </div>
      )}
    </>
  );
};

// Speaker labels carry a timestamp ("Anna · 00:02") because an imported file
// is an archive you scrub through. A session that is still running is not —
// and the label's clock would have to agree with the recorder's, which the
// persona variants (00:09, 00:11, 00:15) make impossible at any single speed.
// Every locale uses the same separator, verified across the catalog.
const speakerName = (label: string) => label.split("·")[0].trim() || label;

const SpeakerLine = ({
  speaker,
  line,
  withTime = true,
}: {
  speaker: string;
  line: string;
  withTime?: boolean;
}) => (
  <div>
    <span className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--tint)]">
      {withTime ? speaker : speakerName(speaker)}
    </span>
    <p className="mt-0.5 text-[13px] leading-snug text-[var(--text-primary)]">
      {line}
    </p>
  </div>
);

// ── Import: the file you'd have to type out → transcribing → finished notes ──
const ImportDemo = ({ persona }: { persona: Persona | null }) => {
  const { t } = useTranslation();
  const tp = usePersonaFlavor(persona);
  const beat = useBeats([300, 1500, 550, 650, 800, 700]);
  return (
    <Card>
      <div className="flex items-center gap-3 px-4 py-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--tint-soft)]">
          <FileTextIcon size={19} className="text-[var(--tint)]" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13.5px] font-semibold text-[var(--text-primary)]">
            {tp("onboarding.ahaDemo.import_fileName")}
          </span>
          <span className="text-[11.5px] text-[var(--text-tertiary)]">
            {beat < 2
              ? t("onboarding.ahaDemo.import_processing")
              : t("onboarding.ahaDemo.import_done")}
          </span>
        </div>
        {beat >= 2 && (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--success)]/20 animate-in zoom-in duration-300">
            <CheckIcon size={12} className="text-[var(--success)]" />
          </div>
        )}
      </div>

      {beat >= 1 && beat < 2 && (
        <div className="mx-4 mb-3 h-1.5 overflow-hidden rounded-full bg-[var(--fill-2)]">
          <div className="aha-progress-fill h-full rounded-full bg-[var(--tint)]" />
        </div>
      )}

      {beat >= 3 && (
        <Divider>
          <div className="flex flex-col gap-2.5">
            <SpeakerLine
              speaker={tp("onboarding.ahaDemo.import_speaker1")}
              line={tp("onboarding.ahaDemo.import_line1")}
            />
            {beat >= 4 && (
              <div className="animate-in fade-in slide-in-from-bottom-1 duration-500">
                <SpeakerLine
                  speaker={tp("onboarding.ahaDemo.import_speaker2")}
                  line={tp("onboarding.ahaDemo.import_line2")}
                />
              </div>
            )}
          </div>
        </Divider>
      )}

      <NotesArtifact beat={beat - 4} persona={persona} />
    </Card>
  );
};

// ── Record: a session running → stopped → the notes it left behind ────────
// The clock ticks at 2× so the line timestamps still read true while the
// whole demo stays under seven seconds.
const RecordDemo = ({ persona }: { persona: Persona | null }) => {
  const { t } = useTranslation();
  const tp = usePersonaFlavor(persona);
  const beat = useBeats([1000, 2500, 1000, 800, 700]);
  const recording = beat < 3;
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setSeconds(9);
      return;
    }
    if (!recording) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 500);
    return () => clearInterval(id);
  }, [recording]);

  const clock = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(
    seconds % 60,
  ).padStart(2, "0")}`;

  return (
    <Card>
      <div className="flex items-center gap-3 px-4 py-3.5">
        <div
          className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            recording
              ? "bg-[var(--dictation-listening)]"
              : "bg-[var(--success)]"
          }`}
        >
          {recording && (
            <span className="absolute inset-0 rounded-full bg-[var(--dictation-listening)] opacity-40 motion-safe:animate-ping" />
          )}
          {recording ? (
            <MicrophoneFilledIcon size={18} className="relative text-white" />
          ) : (
            <CheckIcon size={18} className="relative text-white" />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[13.5px] font-semibold text-[var(--text-primary)]">
            {recording
              ? t("onboarding.ahaDemo.record_status")
              : beat < 4
                ? t("onboarding.ahaDemo.record_making")
                : t("onboarding.ahaDemo.record_done")}
          </span>
          <span className="text-[11.5px] tabular-nums text-[var(--text-tertiary)]">
            {clock}
          </span>
        </div>
        <Waveform live={recording} bars={12} />
      </div>

      {beat >= 1 && (
        <Divider>
          <div className="flex flex-col gap-2.5">
            <SpeakerLine
              withTime={false}
              speaker={tp("onboarding.ahaDemo.import_speaker1")}
              line={tp("onboarding.ahaDemo.import_line1")}
            />
            {beat >= 2 && (
              <div className="animate-in fade-in slide-in-from-bottom-1 duration-500">
                <SpeakerLine
                  withTime={false}
                  speaker={tp("onboarding.ahaDemo.import_speaker2")}
                  line={tp("onboarding.ahaDemo.import_line2")}
                />
              </div>
            )}
          </div>
        </Divider>
      )}

      <NotesArtifact beat={beat - 3} persona={persona} />
    </Card>
  );
};

// ── Dictate: raw speech → cleaning up → the sentence you'd have typed ─────
// The messy middle IS the demo. A perfectly-formed sentence appearing on its
// own is what a keyboard does; removing "um so uh i think" is what costs
// money to build and is the only reason to pay for this over the free
// microphone already on the phone.
type DictatePhase = "idle" | "listening" | "raw" | "polishing" | "done";

const DictateDemo = ({ persona }: { persona: Persona | null }) => {
  const { t } = useTranslation();
  const tp = usePersonaFlavor(persona);
  const raw = tp("onboarding.ahaDemo.dictate_raw");
  const polished = tp("onboarding.ahaDemo.dictate_demoLine");
  const [phase, setPhase] = useState<DictatePhase>("idle");
  // Two of these are scheduled from the typewriter's completion callback
  // rather than the mount effect, so they need somewhere to be cancelled from
  // when Continue is tapped mid-sentence.
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setPhase("done");
      return;
    }
    timers.current.push(
      setTimeout(() => setPhase("listening"), 900),
      setTimeout(() => setPhase("raw"), 1900),
    );
    const scheduled = timers.current;
    return () => scheduled.forEach(clearTimeout);
  }, []);

  // Chained off the real end of the typed line, not a fixed delay — a locale
  // whose raw sentence is 40% longer must still get its full pause before the
  // cleanup lands.
  const onRawTyped = () => {
    timers.current.push(
      setTimeout(() => setPhase("polishing"), 450),
      setTimeout(() => setPhase("done"), 1400),
    );
  };

  const status = {
    idle: t("onboarding.ahaDemo.dictate_idle"),
    listening: t("onboarding.ahaDemo.listening"),
    raw: t("onboarding.ahaDemo.listening"),
    polishing: t("onboarding.ahaDemo.dictate_polishing"),
    done: t("onboarding.ahaDemo.dictate_inserted"),
  }[phase];

  const listening = phase === "listening" || phase === "raw";

  return (
    <Card>
      {/* The compose surface: deliberately generic — this happens in mail,
          messages, notes and any other field, so naming one app would make it
          look like an integration with that app. */}
      <div className="min-h-[112px] px-4 pb-3 pt-4">
        {phase === "idle" ? (
          <p className="text-[14px] leading-relaxed text-[var(--text-tertiary)]">
            {t("onboarding.ahaDemo.dictate_placeholder")}
            <span className="aha-caret ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[2px] bg-[var(--text-primary)]" />
          </p>
        ) : phase === "done" ? (
          <p className="text-[14px] leading-relaxed text-[var(--text-primary)] animate-in fade-in duration-500">
            {polished}
          </p>
        ) : (
          <p
            className={`text-[14px] leading-relaxed transition-opacity duration-500 ${
              phase === "polishing"
                ? "text-[var(--text-tertiary)] opacity-50"
                : "text-[var(--text-secondary)]"
            }`}
          >
            <Typewriter
              active={phase === "raw" || phase === "polishing"}
              text={raw}
              speedMs={22}
              onDone={onRawTyped}
            />
          </p>
        )}
      </div>

      {/* The 1T keyboard bar. It is the thing they will actually tap after
          onboarding, so the demo should look like it. */}
      <div className="flex items-center gap-2.5 border-t border-[var(--hairline)] bg-[var(--fill-1)] px-4 py-3">
        <div
          className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            listening
              ? "bg-[var(--dictation-listening)]"
              : phase === "done"
                ? "bg-[var(--success)]"
                : "bg-[var(--fill-3)]"
          }`}
        >
          {listening && (
            <span className="absolute inset-0 rounded-full bg-[var(--dictation-listening)] opacity-40 motion-safe:animate-ping" />
          )}
          {phase === "done" ? (
            <CheckIcon size={16} className="relative text-white" />
          ) : (
            <MicrophoneFilledIcon
              size={16}
              className={
                listening
                  ? "relative text-white"
                  : "relative text-[var(--text-secondary)]"
              }
            />
          )}
        </div>
        {listening ? <Waveform live bars={12} /> : null}
        <span
          className={`text-[11.5px] font-bold ${
            phase === "done"
              ? "text-[var(--success)]"
              : "text-[var(--text-secondary)]"
          }`}
        >
          {status}
        </span>
      </div>
    </Card>
  );
};

// ── Study: a lecture you'd have to re-watch → pipeline → a flashcard ──────
const StudyDemo = ({ persona }: { persona: Persona | null }) => {
  const { t } = useTranslation();
  const tp = usePersonaFlavor(persona);
  const beat = useBeats([400, 800, 700, 700, 900, 900]);
  const stages = [
    { key: "study_stage1", Icon: FileTextIcon },
    { key: "study_stage2", Icon: SparklesIcon },
    { key: "study_stage3", Icon: CardIcon },
  ];
  return (
    <div className="flex w-full flex-col gap-2.5">
      {/* The rail sits above the content it describes, and wraps rather than
          overflowing — three nowrap pills plus arrows are wider than a 360px
          phone before a single word is translated. */}
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {stages.map(({ key, Icon }, i) => (
          <div key={key} className="flex max-w-full items-center gap-1.5">
            {i > 0 && (
              <span
                className={`text-[var(--text-tertiary)] transition-opacity duration-300 ${
                  beat >= i + 1 ? "opacity-100" : "opacity-20"
                }`}
              >
                →
              </span>
            )}
            <div
              className={`flex min-w-0 items-center gap-1.5 rounded-full px-2.5 py-1.5 transition-all duration-300 ${
                beat >= i + 1
                  ? "bg-[var(--tint-soft)] opacity-100"
                  : "glass-tile opacity-30"
              }`}
            >
              <Icon
                size={13}
                className={`shrink-0 ${
                  beat >= i + 1
                    ? "text-[var(--tint)]"
                    : "text-[var(--text-tertiary)]"
                }`}
              />
              <span className="text-[10.5px] font-bold leading-tight text-[var(--text-primary)]">
                {t(`onboarding.ahaDemo.${key}`)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <Card>
        <div className="px-4 py-3.5">
          <span className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--tint)]">
            {tp("onboarding.ahaDemo.study_sourceLabel")}
          </span>
          <p className="mt-0.5 text-[13px] leading-snug text-[var(--text-secondary)]">
            {tp("onboarding.ahaDemo.study_sourceLine")}
          </p>
        </div>
        {beat >= 4 && (
          <Divider>
            <p className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wider text-[var(--premium-primary)]">
              <SparklesIcon size={12} />
              {t("onboarding.ahaDemo.summaryLabel")}
            </p>
            <p className="mt-1 text-[12.5px] leading-snug text-[var(--text-primary)]">
              {t("onboarding.ahaDemo.study_summaryLine")}
            </p>
          </Divider>
        )}
      </Card>

      {beat >= 5 && (
        <Card className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="px-4 py-3.5">
            <p className="text-[13.5px] font-semibold leading-snug text-[var(--text-primary)]">
              {t("onboarding.ahaDemo.study_demoQuestion")}
            </p>
            {beat >= 6 && (
              <p className="mt-2 flex items-start gap-1.5 text-[13px] font-medium leading-snug text-[var(--success)] animate-in fade-in duration-500">
                <CheckIcon size={14} className="mt-0.5 shrink-0" />
                {t("onboarding.ahaDemo.study_demoAnswer")}
              </p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

// ── Translate: one source line → translating → the same thing, everywhere ──
// Target-language lines are intentionally NOT i18n keys — they must stay in
// their fixed languages regardless of the user's locale.
const TRANSLATE_TARGETS = [
  {
    lang: "Español",
    line: "Bienvenidos de nuevo al canal — hoy probamos algo especial.",
  },
  {
    lang: "Deutsch",
    line: "Willkommen zurück auf dem Kanal — heute testen wir etwas Besonderes.",
  },
  {
    lang: "日本語",
    line: "チャンネルへおかえりなさい — 今日は特別なものを試します。",
  },
];

const TranslateDemo = () => {
  const { t } = useTranslation();
  const beat = useBeats([400, 900, 700, 700]);
  return (
    <div className="flex w-full flex-col gap-2.5">
      <Card>
        <div className="px-4 py-3.5">
          <span className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
            {t("onboarding.ahaDemo.translate_sourceLabel")}
          </span>
          <p className="mt-0.5 text-[13.5px] leading-snug text-[var(--text-primary)]">
            {t("onboarding.ahaDemo.translate_sourceLine")}
          </p>
        </div>
      </Card>

      {beat >= 1 && beat < 2 && (
        <div className="flex items-center justify-center gap-2 py-1 animate-in fade-in duration-300">
          <TranslateIcon size={13} className="text-[var(--tint)]" />
          <span className="text-[11.5px] font-bold text-[var(--text-secondary)]">
            {t("onboarding.ahaDemo.translate_working")}
          </span>
        </div>
      )}

      {TRANSLATE_TARGETS.map((target, i) =>
        beat >= i + 2 ? (
          <Card
            key={target.lang}
            className="animate-in fade-in slide-in-from-bottom-2 duration-500"
          >
            <div className="px-4 py-3.5">
              <span className="flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider text-[var(--tint)]">
                <TranslateIcon size={11} /> {target.lang}
              </span>
              <p className="mt-0.5 text-[13.5px] leading-snug text-[var(--text-primary)]">
                {target.line}
              </p>
            </div>
          </Card>
        ) : null,
      )}
    </div>
  );
};

const AhaDemo = ({
  intent,
  persona,
}: {
  intent: OnboardingIntent;
  persona: Persona | null;
}) => {
  const { t } = useTranslation();
  const renderDemo = () => {
    switch (intent) {
      case "import":
        return <ImportDemo persona={persona} />;
      case "record":
        return <RecordDemo persona={persona} />;
      case "dictate":
        return getPlatform() === "android" ? (
          <AndroidDictationDemoVideo />
        ) : (
          <DictateDemo persona={persona} />
        );
      case "study":
        return <StudyDemo persona={persona} />;
      case "translate":
        return <TranslateDemo />;
    }
  };
  return (
    <div className="flex w-full flex-col items-center text-center">
      <style>{`
        @keyframes aha-progress {
          from { width: 4%; }
          to { width: 100%; }
        }
        .aha-progress-fill {
          animation: aha-progress 1.6s ease-out both;
        }
        @keyframes aha-wave {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
        .aha-wave-bar {
          transform-origin: center;
          animation: aha-wave 820ms ease-in-out infinite;
        }
        @keyframes aha-caret {
          0%, 45% { opacity: 1; }
          46%, 100% { opacity: 0; }
        }
        .aha-caret {
          animation: aha-caret 1s steps(2, start) infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .aha-progress-fill,
          .aha-wave-bar,
          .aha-caret {
            animation: none;
          }
          .aha-wave-bar {
            transform: scaleY(0.6);
          }
        }
      `}</style>
      <span className="mb-4 rounded-full bg-[var(--accent)]/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[var(--accent)]">
        {t("onboarding.ahaDemo.pill")}
      </span>
      <h2 className="mb-5 max-w-[94%] text-balance text-2xl font-black tracking-tight text-[var(--text-primary)]">
        {t(`onboarding.ahaDemo.${intent}_headline`)}
      </h2>
      {renderDemo()}
      <p className="mt-5 max-w-[92%] text-[12.5px] leading-relaxed text-[var(--text-tertiary)]">
        {t(`onboarding.ahaDemo.${intent}_caption`)}
      </p>
    </div>
  );
};

export default AhaDemo;
